import { RmsPersonProfileSummary, RmvDriverLicenseResult, Mark43File } from '@mark43/dex-api';
import {
    Caution,
    Flex,
    HStack,
    Link,
    VStack,
    Box,
    Avatar,
    Card,
    CardBody,
    InlineBanner,
    Text,
    DataList,
    DataItem,
} from '@cad-arc';
import { useCopyText } from '../../common/hooks/use-copy-text';
import { LinkText } from '../../common/components/link-text';
import { useOpenDepartmentLink } from '../../layout/use-open-department-link';

const SUCCESS_STATES = ['ACT'];
const ATTENTION_STATES = ['EXP', 'SUS', 'REV', 'DEN'];

const getDriverLicenseStatusInlineBannerStatus = (
    operatorLicenseStatus?: string
): 'success' | 'attention' | undefined => {
    if (!operatorLicenseStatus) {
        return undefined;
    }
    if (SUCCESS_STATES.includes(operatorLicenseStatus)) {
        return 'success';
    }
    if (ATTENTION_STATES.includes(operatorLicenseStatus)) {
        return 'attention';
    }
    return undefined;
};

export function DexParsedPersonSummary({
    rmvDriverLicenseResult,
    rmsPersonProfileSummary,
    rmvImage,
}: {
    rmvDriverLicenseResult: RmvDriverLicenseResult;
    rmsPersonProfileSummary?: RmsPersonProfileSummary;
    rmvImage?: Mark43File;
}) {
    const openDepartmentLink = useOpenDepartmentLink();
    const copyText = useCopyText();
    const inlineBannerStatus = getDriverLicenseStatusInlineBannerStatus(
        rmvDriverLicenseResult.operatorLicenseStatus
    );

    return (
        <Card>
            {inlineBannerStatus && (
                <Box p="2">
                    <InlineBanner
                        status={inlineBannerStatus}
                        title={`DL Status: ${rmvDriverLicenseResult.operatorLicenseStatus}`}
                    />
                </Box>
            )}
            <CardBody>
                <VStack align="left">
                    <VStack align="left">
                        <Flex alignItems="flex-start" gap="10px">
                            {rmvImage && (
                                <Flex>
                                    <Avatar
                                        name={rmvImage.originalFileName}
                                        src={rmvImage.fileResourcePath}
                                        size="2xl"
                                    />
                                </Flex>
                            )}
                            <Flex flexDirection="column" gap="10px">
                                {rmsPersonProfileSummary?.cautions && (
                                    <Flex flexWrap="wrap" gap="5px">
                                        {rmsPersonProfileSummary.cautions.map((caution) => (
                                            <Caution key={caution} size="sm" priority={1}>
                                                {caution}
                                            </Caution>
                                        ))}
                                    </Flex>
                                )}

                                <Flex flexDirection="row" gap="48px">
                                    <Flex flexDirection="column" gap="10px">
                                        <Text variant="headingSm">
                                            {rmsPersonProfileSummary?.masterPersonProfileId ? (
                                                <LinkText
                                                    onClick={() => {
                                                        openDepartmentLink({
                                                            path: `profiles/persons/${rmsPersonProfileSummary.masterPersonProfileId}/details`,
                                                            app: 'RMS',
                                                            tenantStrategy: 'PREFER_RMS_TENANT',
                                                        });
                                                    }}
                                                >
                                                    {rmvDriverLicenseResult.name}
                                                </LinkText>
                                            ) : (
                                                rmvDriverLicenseResult.name
                                            )}
                                        </Text>

                                        <DataList>
                                            <DataItem dataKey="DOB">
                                                {rmvDriverLicenseResult.birthDate}
                                            </DataItem>
                                            <DataItem dataKey="SSN">
                                                <Link
                                                    onClick={() => {
                                                        if (
                                                            rmvDriverLicenseResult.socialSecurityNumber
                                                        ) {
                                                            copyText({
                                                                text:
                                                                    rmvDriverLicenseResult.socialSecurityNumber,
                                                                copiedFieldLabel: 'SSN',
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {rmvDriverLicenseResult.socialSecurityNumber}
                                                </Link>
                                            </DataItem>
                                            <DataItem dataKey="Sex">
                                                {rmvDriverLicenseResult.sexCode}
                                            </DataItem>
                                            <DataItem dataKey="HT">
                                                {rmvDriverLicenseResult.height}
                                            </DataItem>
                                        </DataList>
                                    </Flex>

                                    <DataList variant="stacked">
                                        <DataItem dataKey="Address">
                                            <Text lineHeight={1.3}>
                                                {rmvDriverLicenseResult.addressStreet}
                                                {'\n'}
                                                {rmvDriverLicenseResult.addressCity},{' '}
                                                {rmvDriverLicenseResult.addressStateCode}
                                            </Text>
                                        </DataItem>
                                    </DataList>
                                </Flex>
                            </Flex>
                        </Flex>
                    </VStack>
                    <HStack alignItems="flex-start" gap="8px">
                        <DataList>
                            <DataItem dataKey="DL Status">
                                {rmvDriverLicenseResult.operatorLicenseStatus}
                            </DataItem>
                            <DataItem dataKey="OLN">
                                {rmvDriverLicenseResult.operatorLicenseNumber}
                            </DataItem>
                        </DataList>
                        <DataList>
                            <DataItem dataKey="Exp. Date">
                                {rmvDriverLicenseResult.expirationDate}
                            </DataItem>
                            <DataItem dataKey="Iss. Date">
                                {rmvDriverLicenseResult.issueDate}
                            </DataItem>
                        </DataList>
                        <DataList>
                            <DataItem dataKey="Class">
                                {rmvDriverLicenseResult.operatorLicenseClassCode}
                            </DataItem>
                        </DataList>
                    </HStack>
                </VStack>
            </CardBody>
        </Card>
    );
}
